import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import TaskListView from './TaskListView';
import TaskTimelineView from './TaskTimelineView';
import CreateTaskModal from './CreateTaskModal';
import TaskDetailModal from './TaskDetailModal';
import TagManagerModal from './TagManagerModal';
import CompletedTasksModal from './CompletedTasksModal';
import {
  FiPlus,
  FiFilter,
  FiSearch,
  FiTag
} from 'react-icons/fi';

const TaskBoard = ({ board, viewMode, canEdit, onBoardUpdate }) => {
  const { user } = useAuth();

  const [boardData, setBoardData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [dependencies, setDependencies] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [showTagManager, setShowTagManager] = useState(false);
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTag, setFilterTag] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch board data with tasks
  useEffect(() => {
    if (board?.id) {
      fetchBoardData();
      fetchTags();
    }
  }, [board?.id]);

  const fetchBoardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/planwise/boards/${board.id}`);
      setBoardData(response.data);
      setTasks(response.data?.tasks || []);
      setDependencies(response.data?.dependencies || []);
    } catch (error) {
      console.error('Error fetching board data:', error);

      let errorMessage = 'Failed to load board data';

      if (error.response) {
        switch (error.response.status) {
          case 404:
            errorMessage = 'Board not found. It may have been deleted or you may not have access to it.';
            break;
          case 403:
            errorMessage = 'You do not have permission to view this board. Please contact the board owner or your team admin.';
            break;
          case 401:
            errorMessage = 'Your session has expired. Please refresh the page to continue.';
            break;
          case 500:
            errorMessage = 'Server error occurred while loading board data. Please try again later.';
            break;
          default:
            errorMessage = `Failed to load board data: ${error.response.data?.message || 'Unknown error occurred'}`;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get('/api/planwise/tags');
      setTags(response.data || []);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const handleCreateTask = async (taskData) => {
    try {
      const response = await axios.post('/api/planwise/tasks', {
        ...taskData,
        board_id: board.id
      });

      setTasks([...tasks, response.data]);
      setShowCreateTask(false);
      fetchBoardData();
    } catch (error) {
      console.error('Error creating task:', error);

      let errorMessage = 'Failed to create task';

      if (error.response) {
        switch (error.response.status) {
          case 403:
            errorMessage = 'You do not have permission to create tasks on this board. Please contact the board owner or your team admin.';
            break;
          case 401:
            errorMessage = 'Your session has expired. Please refresh the page and try again.';
            break;
          case 400:
            errorMessage = `Invalid task data: ${error.response.data?.message || 'Please check your input and try again'}`;
            break;
          case 404:
            errorMessage = 'Board not found. It may have been deleted.';
            break;
          case 500:
            errorMessage = 'Server error occurred while creating the task. Please try again later.';
            break;
          default:
            errorMessage = `Failed to create task: ${error.response.data?.message || 'Unknown error occurred'}`;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      setError(errorMessage);
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      const response = await axios.put(`/api/planwise/tasks/${taskId}`, updates);

      setTasks(tasks.map(task =>
        task.id === taskId ? { ...task, ...response.data } : task
      ));

      setError(null);

      if (selectedTask?.id === taskId) {
        setSelectedTask({ ...selectedTask, ...response });
      }
    } catch (error) {
      console.error('Error updating task:', error);

      let errorMessage = 'Failed to update task';

      if (error.response) {
        switch (error.response.status) {
          case 404:
            errorMessage = 'Task not found. It may have been deleted or you may not have access to it.';
            break;
          case 403:
            errorMessage = 'You do not have permission to update this task. Please contact the task owner or your team admin.';
            break;
          case 401:
            errorMessage = 'Your session has expired. Please refresh the page and try again.';
            break;
          case 400:
            errorMessage = `Invalid task data: ${error.response.data?.message || 'Please check your input and try again'}`;
            break;
          case 409:
            errorMessage = 'Task update conflict. Another user may have modified this task. Please refresh and try again.';
            break;
          case 500:
            errorMessage = 'Server error occurred while updating the task. Please try again later.';
            break;
          default:
            errorMessage = `Failed to update task: ${error.response.data?.message || 'Unknown error occurred'}`;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      setError(errorMessage);
    }
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setShowTaskDetail(true);
  };

  const handleTaskStatusChange = (taskId, newStatus) => {
    handleUpdateTask(taskId, { status: newStatus });
  };

  const handleTaskPositionChange = (taskId, position) => {
    handleUpdateTask(taskId, {
      position_x: position.x,
      position_y: position.y
    });
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`/api/planwise/tasks/${taskId}`);

      setTasks(tasks.filter(task => task.id !== taskId));
      setError(null);

      if (selectedTask?.id === taskId) {
        setSelectedTask(null);
        setShowTaskDetail(false);
      }

      fetchBoardData();
    } catch (error) {
      console.error('Error deleting task:', error);

      let errorMessage = 'Failed to delete task';

      if (error.response) {
        switch (error.response.status) {
          case 404:
            errorMessage = 'Task not found. It may have already been deleted or you may not have access to it.';
            break;
          case 403:
            errorMessage = 'You do not have permission to delete this task. Only task owners and team admins can delete tasks.';
            break;
          case 401:
            errorMessage = 'Your session has expired. Please refresh the page and try again.';
            break;
          case 409:
            errorMessage = 'Cannot delete task as it has dependencies. Please remove dependencies first or contact your team admin.';
            break;
          case 500:
            errorMessage = 'Server error occurred while deleting the task. Please try again later.';
            break;
          default:
            errorMessage = `Failed to delete task: ${error.response.data?.message || 'Unknown error occurred'}`;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      setError(errorMessage);
    }
  };

  // Filter tasks based on search and filters
  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         task.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' || task.status === filterStatus;

    const matchesTag = filterTag === 'all' ||
                      task.tags?.some(tag => tag.id === filterTag);

    return matchesSearch && matchesStatus && matchesTag;
  });

  // Group tasks by status for list view
  const tasksByStatus = {
    pending: filteredTasks.filter(task => task.status === 'pending'),
    in_progress: filteredTasks.filter(task => task.status === 'in_progress'),
    completed: filteredTasks.filter(task => task.status === 'completed'),
    delegated: filteredTasks.filter(task => task.status === 'delegated')
  };

  const statusColors = {
    pending: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
    in_progress: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    completed: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    delegated: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-5">
        <div className="animate-pulse space-y-3">
          <div className="h-5 bg-gray-300 dark:bg-gray-700 rounded w-1/4"></div>
          <div className="h-3 bg-gray-300 dark:bg-gray-700 rounded w-1/2"></div>
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-10 bg-gray-300 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-5 text-center">
        <div className="text-red-600 dark:text-red-400">
          <p className="text-base font-semibold mb-1">Error Loading Board</p>
          <p className="text-sm mb-3">{error}</p>
          <button
            onClick={fetchBoardData}
            className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Board Header */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-4">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-4 h-4 rounded-sm flex-shrink-0"
              style={{ backgroundColor: board.color }}
            />
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                {board.name}
              </h2>
              {board.description && (
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 truncate">
                  {board.description}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Task Stats */}
            <div className="flex items-center gap-2 px-2.5 py-1 bg-gray-100 dark:bg-gray-700/50 rounded-md text-xs">
              <span className="font-semibold text-gray-900 dark:text-white">{tasks.length}</span>
              <span className="text-gray-600 dark:text-gray-400">tasks</span>
            </div>
            <button
              onClick={() => setShowCompletedTasks(true)}
              className="flex items-center gap-2 px-2.5 py-1 bg-emerald-100 dark:bg-emerald-900/30 rounded-md hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-colors text-xs"
            >
              <span className="font-semibold text-emerald-700 dark:text-emerald-400">{tasksByStatus.completed.length}</span>
              <span className="text-emerald-700 dark:text-emerald-400">completed</span>
            </button>

            {canEdit && (
              <>
                <button
                  onClick={() => setShowCreateTask(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium transition-colors"
                >
                  <FiPlus className="w-4 h-4" />
                  Add Task
                </button>

                <button
                  onClick={() => setShowTagManager(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  <FiTag className="w-4 h-4 text-purple-500" />
                  Tags
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3">
        <div className="flex flex-col sm:flex-row gap-2">
          {/* Search */}
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm text-gray-900 dark:text-white placeholder-gray-400"
            />
          </div>

          {/* Filters */}
          <div className="flex gap-2">
            <div className="relative">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="appearance-none pl-3 pr-9 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm text-gray-900 dark:text-white min-w-[130px]"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="delegated">Delegated</option>
              </select>
              <FiFilter className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5 pointer-events-none" />
            </div>

            <div className="relative">
              <select
                value={filterTag}
                onChange={(e) => setFilterTag(e.target.value)}
                className="appearance-none pl-3 pr-9 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm text-gray-900 dark:text-white min-w-[110px]"
              >
                <option value="all">All Tags</option>
                {tags.map(tag => (
                  <option key={tag.id} value={tag.id}>
                    {tag.name}
                  </option>
                ))}
              </select>
              <FiTag className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5 pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* Task Views */}
      {viewMode === 'list' && (
        <TaskListView
          tasksByStatus={tasksByStatus}
          statusColors={statusColors}
          onTaskClick={handleTaskClick}
          onStatusChange={handleTaskStatusChange}
          onTaskUpdate={handleUpdateTask}
          canEdit={canEdit}
        />
      )}

      {viewMode === 'timeline' && (
        <TaskTimelineView
          tasks={filteredTasks}
          onTaskClick={handleTaskClick}
          canEdit={canEdit}
        />
      )}

      {/* Empty State */}
      {filteredTasks.length === 0 && !loading && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-8 text-center">
          <FiPlus className="w-10 h-10 mx-auto mb-3 text-gray-400 dark:text-gray-500" />
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">No Tasks Found</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {searchTerm || filterStatus !== 'all' || filterTag !== 'all'
              ? 'No tasks match your current filters'
              : 'Get started by creating your first task'
            }
          </p>
          {canEdit && !searchTerm && filterStatus === 'all' && filterTag === 'all' && (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => setShowCreateTask(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium transition-colors"
              >
                <FiPlus className="w-4 h-4" />
                Add Task
              </button>
              <button
                onClick={() => setShowTagManager(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                <FiTag className="w-4 h-4 text-purple-500" />
                Tags
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {showCreateTask && (
        <CreateTaskModal
          onClose={() => setShowCreateTask(false)}
          onSubmit={handleCreateTask}
          tags={tags}
        />
      )}

      {showTaskDetail && selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => setShowTaskDetail(false)}
          onUpdate={handleUpdateTask}
          onDelete={handleDeleteTask}
          canEdit={canEdit}
          tags={tags}
        />
      )}

      {showTagManager && (
        <TagManagerModal
          onClose={() => setShowTagManager(false)}
          onTagsUpdated={fetchTags}
        />
      )}

      {showCompletedTasks && (
        <CompletedTasksModal
          isOpen={showCompletedTasks}
          onClose={() => setShowCompletedTasks(false)}
          completedTasks={tasksByStatus.completed}
          onTaskClick={(task) => {
            setSelectedTask(task);
            setShowTaskDetail(true);
            setShowCompletedTasks(false);
          }}
        />
      )}
    </div>
  );
};

export default TaskBoard;
